import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { TimeRange, AnalyticsCategory, setCurrentCategory, setCurrentTimeRange } from '@/redux/slices/analyticsSlice';
import {
  fetchTokenPurchases,
  fetchTrades,
  fetchPosts,
  fetchMessages,
  fetchFollowers,
  fetchNotifications,
  fetchEngagement,
  fetchRoyalties,
  fetchContentEarnings,
  fetchProfileViews,
  fetchShareClicks,
  fetchReputation,
} from '@/redux/thunks/analyticsThunks';
import AnalyticsTable, { TableColumn } from './components/AnalyticsTable';
import { FaCoins, FaExchangeAlt, FaFileAlt, FaEnvelope, FaUsers, FaBell, FaChartLine, FaStar, FaLock, FaEye, FaVideo, FaComments, FaShareAlt, FaKey, FaAward } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './page.scss';

// Human-readable labels for the profile_view.source enum. Falls back to
// 'Direct' for unknown / NULL legacy values.
const SOURCE_LABELS: Record<string, string> = {
  direct: 'Direct / shared link',
  feed: 'Feed post',
  hashtag: 'Hashtag feed',
  marketplace: 'Marketplace',
  previously_owned: 'Previously owned',
  discovery: 'Discovery',
  top_profiles: 'Top profiles',
  leaderboard: 'Leaderboard',
  chat: 'Chat',
  messages: 'DM thread',
  provenance: 'Profile provenance',
  user_profiles: 'NFT profiles tab',
  user_activity: 'Activity feed',
  follow_list: 'Followers / Following',
  achievements: 'Achievements',
  analytics: 'Analytics',
  spotlight: 'Spotlight ✨',
};
const formatSource = (source: string | null | undefined): string =>
  SOURCE_LABELS[source ?? 'direct'] ?? 'Direct / shared link';

const AnalyticsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [royaltyType, setRoyaltyType] = React.useState<
    'all' | 'profile_sales' | 'spotlights' | 'dm_spotlight' | 'post_spotlight' | 'profile_spotlight'
  >('all');
  const {
    currentCategory,
    currentTimeRange,
    tokenPurchases,
    trades,
    posts,
    messages,
    followers,
    notifications,
    engagement,
    royalties,
    contentEarnings,
    profileViews,
    shareClicks,
    reputation,
    isLoading,
    error,
  } = useAppSelector((state) => state.analytics);

  // Handle category from URL query parameter (only on initial load)
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categoryParam !== currentCategory) {
      dispatch(setCurrentCategory(categoryParam as AnalyticsCategory));
    }
  }, [searchParams.get('category'), dispatch]);

  // Fetch data when category or time range changes
  useEffect(() => {
    switch (currentCategory) {
      case 'royalties':
        dispatch(fetchRoyalties({ timeRange: currentTimeRange, type: royaltyType }));
        break;
      case 'content-earnings':
        dispatch(fetchContentEarnings(currentTimeRange));
        break;
      case 'token-purchases':
        dispatch(fetchTokenPurchases(currentTimeRange));
        break;
      case 'trades':
        dispatch(fetchTrades(currentTimeRange));
        break;
      case 'posts':
        dispatch(fetchPosts(currentTimeRange));
        break;
      case 'messages':
        dispatch(fetchMessages(currentTimeRange));
        break;
      case 'followers':
        dispatch(fetchFollowers(currentTimeRange));
        break;
      case 'notifications':
        dispatch(fetchNotifications(currentTimeRange));
        break;
      case 'engagement':
        dispatch(fetchEngagement(currentTimeRange));
        break;
      case 'profile-views':
        dispatch(fetchProfileViews(currentTimeRange));
        break;
      case 'share-clicks':
        dispatch(fetchShareClicks(currentTimeRange));
        break;
      case 'reputation':
        // Reputation snapshot is a point-in-time score, not a time
        // range — the timeRange picker at the top is a no-op here.
        dispatch(fetchReputation());
        break;
    }
  }, [currentCategory, currentTimeRange, dispatch, royaltyType]);

  const handleCategoryChange = (category: AnalyticsCategory) => {
    dispatch(setCurrentCategory(category));
    // Update URL to reflect the new category
    setSearchParams({ category });
  };

  const handleTimeRangeChange = (timeRange: TimeRange) => {
    dispatch(setCurrentTimeRange(timeRange));
  };

  // Define categories with icons
  const categories = [
    // Reputation is the wedge — pin it first so creators see their
    // scores before they see any earnings breakdown (KLYX_Vision_Roadmap_v0.3).
    { id: 'reputation' as AnalyticsCategory, label: 'Reputation', icon: <FaAward /> },
    { id: 'royalties' as AnalyticsCategory, label: 'Royalties', icon: <FaCoins /> },
    { id: 'content-earnings' as AnalyticsCategory, label: 'Content Sales', icon: <FaLock /> },
    { id: 'trades' as AnalyticsCategory, label: 'NFT Trades', icon: <FaExchangeAlt /> },
    { id: 'posts' as AnalyticsCategory, label: 'Posts', icon: <FaFileAlt /> },
    { id: 'messages' as AnalyticsCategory, label: 'Messages', icon: <FaEnvelope /> },
    { id: 'followers' as AnalyticsCategory, label: 'Followers', icon: <FaUsers /> },
    { id: 'notifications' as AnalyticsCategory, label: 'Notifications', icon: <FaBell /> },
    { id: 'engagement' as AnalyticsCategory, label: 'Engagement', icon: <FaChartLine /> },
    { id: 'profile-views' as AnalyticsCategory, label: 'Profile Views', icon: <FaEye /> },
    { id: 'share-clicks' as AnalyticsCategory, label: 'Share Clicks', icon: <FaShareAlt /> },
    // { id: 'token-purchases' as AnalyticsCategory, label: 'Token Purchases', icon: <FaCoins /> },
  ];

  const subheadings: Record<AnalyticsCategory, { title: string; description: string; icon: React.ReactNode }> = {
    'reputation':       { title: 'Reputation',      description: 'Your on-chain reputation — the same scores that drive discovery, marketplace ranking, and the public Klyx reputation API', icon: <FaAward /> },
    'royalties':        { title: 'Royalties',      description: 'Earnings from profile sales and spotlight promotions', icon: <FaCoins /> },
    'content-earnings': { title: 'Content Sales',  description: 'Revenue from pay-per-view posts, view fees, and subscriptions', icon: <FaLock /> },
    'trades':           { title: 'NFT Trades',     description: 'Profile NFT buy and sell activity', icon: <FaExchangeAlt /> },
    'posts':            { title: 'Posts',           description: 'Your post activity and engagement stats', icon: <FaFileAlt /> },
    'messages':         { title: 'Messages',        description: 'Direct message history sent and received', icon: <FaEnvelope /> },
    'followers':        { title: 'Followers',       description: 'New followers and accounts you started following', icon: <FaUsers /> },
    'notifications':    { title: 'Notifications',   description: 'Platform notifications and read status', icon: <FaBell /> },
    'engagement':       { title: 'Engagement',      description: 'Likes, comments, and favorites given and received', icon: <FaChartLine /> },
    'token-purchases':  { title: 'Token Purchases', description: 'KLYX token purchase history', icon: <FaCoins /> },
    'profile-views':    { title: 'Profile Views',   description: 'Who viewed your profile and for how long', icon: <FaEye /> },
    'share-clicks':     { title: 'Share Clicks',    description: 'Click-throughs on links you’ve shared, broken down by what was shared', icon: <FaShareAlt /> },
  };

  const activeSubheading = subheadings[currentCategory];

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' },
  ];

  // Render content based on category
  const renderContent = () => {
    if (error) {
      return (
        <div className="analytics-error">
          <p>Error: {error}</p>
        </div>
      );
    }

    switch (currentCategory) {
      case 'royalties':
        return renderRoyalties();
      case 'content-earnings':
        return renderContentEarnings();
      case 'token-purchases':
        return renderTokenPurchases();
      case 'trades':
        return renderTrades();
      case 'posts':
        return renderPosts();
      case 'messages':
        return renderMessages();
      case 'followers':
        return renderFollowers();
      case 'notifications':
        return renderNotifications();
      case 'engagement':
        return renderEngagement();
      case 'profile-views':
        return renderProfileViews();
      case 'share-clicks':
        return renderShareClicks();
      case 'reputation':
        return renderReputation();
      default:
        return null;
    }
  };

  const renderShareClicks = () => {
    if (!shareClicks) {
      return <div className="analytics-empty"><p>No share-click data yet.</p></div>;
    }

    const { stats, breakdown } = shareClicks;

    const columns: TableColumn<any>[] = [
      {
        key: 'contentType',
        header: 'Type',
        render: (item) => (
          <span className={`analytics-badge analytics-badge--${item.contentType === 'profile' ? 'sub' : 'viewfee'}`}>
            {item.contentType === 'profile' ? 'PROFILE' : 'POST'}
          </span>
        ),
        width: '90px',
      },
      {
        key: 'label',
        header: 'Shared content',
        render: (item) =>
          item.contentType === 'profile' && item.ownerUsername ? (
            <Link to={`/profile/@${item.ownerUsername}`} className="analytics-user-link">
              {item.label}
            </Link>
          ) : item.contentType === 'post' ? (
            <Link to={`/post/${item.contentId}`} className="analytics-user-link">
              {item.label || '(no preview)'}
            </Link>
          ) : (
            <span>{item.label}</span>
          ),
      },
      {
        key: 'clicks',
        header: 'Click-throughs',
        render: (item) => (
          <span style={{ fontWeight: 600 }}>{item.clicks}</span>
        ),
        width: '140px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Period Clicks</span>
            <span className="stat-value">{stats.periodClicks}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">All-Time Clicks</span>
            <span className="stat-value">{stats.totalClicks}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Unique Items Shared</span>
            <span className="stat-value">{stats.uniqueContent}</span>
          </div>
        </div>

        <h3 className="analytics-subsection-title">Per-content breakdown</h3>
        <AnalyticsTable
          columns={columns}
          data={breakdown}
          isLoading={isLoading}
          emptyMessage="No click-throughs in this period."
        />
      </div>
    );
  };

  /**
   * Reputation tab (ADR-008 option B surfacing). Each score card shows
   * value + breakdown + a short "what this measures" explainer. The
   * inputs list at the bottom is copied straight from the server's
   * REPLICABLE_INPUTS so a third-party integrator reading the client
   * sees the same guarantee the public API makes.
   *
   * `reputation === null` after a successful fetch means the caller
   * hasn't minted an NFT profile yet — render an empty state that
   * points to the create-profile flow rather than a scary error.
   */
  const renderReputation = () => {
    if (isLoading && !reputation) {
      return <div className="analytics-empty"><p>Loading your reputation…</p></div>;
    }
    if (!reputation) {
      return (
        <div className="analytics-empty">
          <p>You haven’t minted your on-chain profile yet.</p>
          <p style={{ marginTop: 8 }}>
            <Link to="/create-profile" className="analytics-user-link">Create your profile</Link>{' '}
            to start earning reputation from posts, sales, and engagement.
          </p>
        </div>
      );
    }

    const { scores, formulaVersion, computedAt, replicable } = reputation;
    const fmtScore = (v: number) => v.toFixed(2);
    const fmtWeight = (v: number) => v.toFixed(3);
    const fmtDate = (iso: string | null) =>
      iso ? new Date(iso).toLocaleString() : '—';
    // Render "Rank #3 of 19 · Top 5%" — omit the top-K% clause when
    // percentile is null (server returns null under
    // PERCENTILE_MIN_POPULATION so small-N noise doesn't look precise).
    const fmtRank = (rank: { position: number; total: number; percentile: number | null }) => {
      const base = `#${rank.position} of ${rank.total}`;
      if (rank.percentile === null) return base;
      const pct = Math.max(1, Math.round(rank.percentile * 100));
      return `${base} · Top ${pct}%`;
    };

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Formula version</span>
            <span className="stat-value">{formulaVersion}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Last recomputed</span>
            <span className="stat-value" style={{ fontSize: '0.95rem' }}>{fmtDate(computedAt)}</span>
          </div>
        </div>

        <div className="reputation-cards">
          {/* KlyxValue — the durable long-lived score. */}
          <div className="reputation-card">
            <div className="reputation-card__header">
              <h3>KlyxValue</h3>
              <span className="reputation-card__value">
                {scores.klyxValue ? fmtScore(scores.klyxValue.value) : '—'}
              </span>
            </div>
            {scores.klyxValue && (
              <div className="reputation-card__rank">{fmtRank(scores.klyxValue.rank)}</div>
            )}
            <p className="reputation-card__blurb">
              Lifetime reputation — royalties earned, profile floor, account age, and sybil / wash-trade penalties.
              Drives the marketplace KlyxScore ranking.
            </p>
            {scores.klyxValue && (
              // Show pull factors (royalties, price, age) — the things
              // a legitimate creator can grow. Anti-abuse readings
              // (wSybil, wWashLifetime) are redacted from BOTH this
              // dashboard AND the public API (v1.2, 2026-08-06) — see
              // ADR-008 amendment. Per-user detection state would let
              // gamers optimise against it; the formula spec at
              // /docs/klyx_value_scoring.md stays public so integrators
              // still get replicability.
              <table className="reputation-card__breakdown">
                <tbody>
                  <tr><td>Royalties total</td><td>{fmtScore(scores.klyxValue.breakdown.royaltiesTotal)}</td></tr>
                  <tr><td>Price (KLV)</td><td>{fmtScore(scores.klyxValue.breakdown.priceKlv)}</td></tr>
                  <tr><td>Age weight</td><td>{fmtWeight(scores.klyxValue.breakdown.wAge)}</td></tr>
                  <tr><td>Klyx-age weight</td><td>{fmtWeight(scores.klyxValue.breakdown.wKlyxAge)}</td></tr>
                </tbody>
              </table>
            )}
            {!scores.klyxValue && (
              <p className="reputation-card__pending">Not computed yet — usually within an hour of your first purchase or spotlight.</p>
            )}
          </div>

          {/* TrendingScore — 7-day rolling. */}
          <div className="reputation-card">
            <div className="reputation-card__header">
              <h3>TrendingScore</h3>
              <span className="reputation-card__value">
                {scores.trendingScore ? fmtScore(scores.trendingScore.value) : '—'}
              </span>
            </div>
            {scores.trendingScore && (
              <div className="reputation-card__rank">{fmtRank(scores.trendingScore.rank)}</div>
            )}
            <p className="reputation-card__blurb">
              7-day rolling reputation — recent engagement, recent purchases, and price momentum.
              Drives Trending feeds.
            </p>
            {scores.trendingScore && (
              // Anti-abuse weights hidden — see KlyxValue card comment.
              <table className="reputation-card__breakdown">
                <tbody>
                  <tr><td>7d engagement score</td><td>{fmtScore(scores.trendingScore.breakdown.scoreEng7d)}</td></tr>
                  <tr><td>7d purchases</td><td>{fmtScore(scores.trendingScore.breakdown.m7d)}</td></tr>
                  <tr><td>Price score</td><td>{fmtScore(scores.trendingScore.breakdown.scorePrice)}</td></tr>
                  <tr><td>Spotlight weight</td><td>{fmtWeight(scores.trendingScore.breakdown.wSpot)}</td></tr>
                </tbody>
              </table>
            )}
            {!scores.trendingScore && (
              <p className="reputation-card__pending">Not computed yet — usually within 5 min of relevant activity.</p>
            )}
          </div>

          {/* HotScore — right-now decay (τ=45min). */}
          <div className="reputation-card">
            <div className="reputation-card__header">
              <h3>HotScore</h3>
              <span className="reputation-card__value">
                {scores.hotScore ? fmtScore(scores.hotScore.value) : '—'}
              </span>
            </div>
            {scores.hotScore && (
              <div className="reputation-card__rank">{fmtRank(scores.hotScore.rank)}</div>
            )}
            <p className="reputation-card__blurb">
              Right-now score — 2-hour engagement, 6-hour purchases, and a τ=45 min decay so
              activity from the last hour weighs the most. Drives the Hot Now carousel.
            </p>
            {scores.hotScore && (
              // Anti-abuse weights hidden — see KlyxValue card comment.
              <table className="reputation-card__breakdown">
                <tbody>
                  <tr><td>2h engagement score</td><td>{fmtScore(scores.hotScore.breakdown.scoreEng2h)}</td></tr>
                  <tr><td>6h purchases</td><td>{fmtScore(scores.hotScore.breakdown.m6h)}</td></tr>
                  <tr><td>Decay factor</td><td>{fmtWeight(scores.hotScore.breakdown.decay)}</td></tr>
                  <tr><td>Post-trade weight</td><td>{fmtWeight(scores.hotScore.breakdown.wPostTrade)}</td></tr>
                  <tr><td>Spotlight weight</td><td>{fmtWeight(scores.hotScore.breakdown.wSpot)}</td></tr>
                </tbody>
              </table>
            )}
            {!scores.hotScore && (
              <p className="reputation-card__pending">Not computed yet — usually within 5 min of relevant activity.</p>
            )}
          </div>
        </div>

        <div className="reputation-inputs">
          <h4>What feeds these scores</h4>
          <p className="reputation-inputs__blurb">
            The same scoring pipeline drives the public reputation API at
            {' '}<code>/api/public/reputation/:profileId</code>. Anyone (or any AI agent) can query
            your reputation using the inputs below — the numbers here are the same numbers.
            Anti-abuse detection (sybil / wash) is not returned by either surface: the per-user
            readings would let bad actors optimize against them.
          </p>

          <div className="reputation-inputs__cols">
            <div>
              <strong>On-chain (survives Klyx)</strong>
              <ul>
                {replicable.onChainInputs.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
            <div>
              <strong>Off-chain (Klyx-only)</strong>
              <ul>
                {replicable.offChainInputs.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContentEarnings = () => {
    if (!contentEarnings) {
      return <div className="analytics-empty"><p>No content sales data available.</p></div>;
    }

    const { stats, sales, viewFeeSales, subscriptionSales } = contentEarnings;

    const ppvColumns: TableColumn<any>[] = [
      {
        key: 'createdAt',
        header: 'Date',
        render: (item) => new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        width: '130px',
      },
      {
        key: 'buyerUsername',
        header: 'Buyer',
        render: (item) => item.buyerUsername
          ? <Link to={`/profile/@${item.buyerUsername}`} className="analytics-link">
              {item.buyerDisplayName || item.buyerUsername}
            </Link>
          : <span className="text-muted">Unknown</span>,
      },
      {
        key: 'amount',
        header: 'Amount',
        render: (item) => `${Number(item.amount).toFixed(2)} KLV`,
        width: '140px',
      },
      {
        key: 'type',
        header: 'Type',
        render: () => <span className="analytics-badge analytics-badge--ppv">PPV</span>,
        width: '70px',
      },
    ];

    const accessColumns: TableColumn<any>[] = [
      {
        key: 'createdAt',
        header: 'Date',
        render: (item) => new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        width: '130px',
      },
      {
        key: 'buyerUsername',
        header: 'User',
        render: (item) => item.buyerUsername
          ? <Link to={`/profile/@${item.buyerUsername}`} className="analytics-link">
              {item.buyerDisplayName || item.buyerUsername}
            </Link>
          : <span className="text-muted">Unknown</span>,
      },
      {
        key: 'amount',
        header: 'Amount',
        render: (item) => `${Number(item.amount).toFixed(2)} KLV`,
        width: '140px',
      },
      {
        key: 'type',
        header: 'Type',
        render: (item) => (
          <span className={`analytics-badge analytics-badge--${item.type === 'subscription' ? 'sub' : 'viewfee'}`}>
            {item.type === 'subscription' ? 'SUB' : 'VIEW'}
          </span>
        ),
        width: '70px',
      },
    ];

    return (
      <div className="analytics-section">
        {/* Summary stats */}
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Earned (period)</span>
            <span className="stat-value">{stats.periodEarned.toFixed(2)} KLV</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">All-Time Earned</span>
            <span className="stat-value">{stats.totalAllTime.toFixed(2)} KLV</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">PPV Sales</span>
            <span className="stat-value">{stats.salesCount}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">View Fee Buyers</span>
            <span className="stat-value">{stats.viewFeeSalesCount}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Subscriptions</span>
            <span className="stat-value">{stats.subscriptionSalesCount}</span>
          </div>
        </div>

        {/* PPV Sales */}
        <h3 className="analytics-subsection-title">Pay-Per-View Sales</h3>
        <AnalyticsTable
          columns={ppvColumns}
          data={sales}
          emptyMessage="No PPV sales in this period."
        />

        {/* View Fee Sales */}
        <h3 className="analytics-subsection-title" style={{ marginTop: '2rem' }}>View Fee Purchases</h3>
        <AnalyticsTable
          columns={accessColumns}
          data={viewFeeSales}
          emptyMessage="No view fee purchases in this period."
        />

        {/* Subscription Sales */}
        <h3 className="analytics-subsection-title" style={{ marginTop: '2rem' }}>Subscriptions</h3>
        <AnalyticsTable
          columns={accessColumns}
          data={subscriptionSales}
          emptyMessage="No subscriptions in this period."
        />
      </div>
    );
  };

  const renderRoyalties = () => {
    const getSourceIcon = (source: string) => {
      if (source === 'spotlight') {
        return <FaStar className="source-icon spotlight" />;
      }
      return <FaExchangeAlt className="source-icon profile" />;
    };

    const getSourceLabel = (record: any) => {
      if (record.source === 'profile_sale') {
        return 'Profile Sale';
      }
      if (record.spotlightType === 'dm_spotlight') {
        return 'DM Spotlight';
      }
      if (record.spotlightType === 'post_spotlight') {
        return 'Post Spotlight';
      }
      if (record.spotlightType === 'profile_spotlight') {
        return 'Profile Spotlight';
      }
      return 'Spotlight';
    };

    const columns: TableColumn<any>[] = [
      {
        key: 'createdAt',
        header: 'Date',
        render: (item) => new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        width: '130px',
      },
      {
        key: 'source',
        header: 'Source',
        render: (item) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {getSourceIcon(item.source)}
            <span>{getSourceLabel(item)}</span>
          </div>
        ),
        width: '180px',
      },
      {
        key: 'profileUsername',
        header: 'Profile',
        render: (item) =>
          item.profileUsername ? (
            <Link to={`/profile/${item.profileUsername}`} className="analytics-user-link">
              {item.profileDisplayName || item.profileUsername}
            </Link>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>
          ),
        width: '150px',
      },
      {
        key: 'buyerUsername',
        header: 'From',
        render: (item) =>
          item.buyerUsername ? (
            <Link to={`/profile/${item.buyerUsername}`} className="analytics-user-link">
              {item.buyerDisplayName || item.buyerUsername}
            </Link>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>
          ),
        width: '150px',
      },
      {
        key: 'salePrice',
        header: 'Sale Price',
        render: (item) =>
          item.salePrice ? (
            `${item.salePrice.toFixed(2)} KLV`
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>
          ),
        width: '120px',
      },
      {
        key: 'amount',
        header: 'Royalty Earned',
        render: (item) => (
          <span style={{ color: 'var(--success-color)', fontWeight: 600 }}>
            +{item.amount.toFixed(2)} KLV
          </span>
        ),
        width: '140px',
      },
    ];

    // Order matches the issue (#277) acceptance flat list. `available:
    // false` rows show a "Coming soon" pill instead of the amount so
    // users see the full set of possible sources, including the ones
    // not paying out yet (spotlight share is not active today).
    const feeTypeRows: Array<{
      key: keyof NonNullable<typeof royalties>['stats']['byType'];
      label: string;
      source: string;
      icon: React.ReactNode;
    }> = [
      { key: 'profileSale',        label: 'Profile Sale',        source: '3% creator royalty on each NFT trade',                  icon: <FaExchangeAlt /> },
      { key: 'paidView',           label: 'Paid View',           source: 'One-time view fee to unlock locked content',            icon: <FaEye /> },
      { key: 'payPerView',         label: 'Pay-Per-View',        source: 'One-time unlock fee on a locked post',                  icon: <FaKey /> },
      { key: 'subscription',       label: 'Subscription',        source: 'Recurring subscription fee',                            icon: <FaLock /> },
      { key: 'dm',                 label: 'DM Purchase',         source: 'Paid DM fee for a one-shot message',                    icon: <FaEnvelope /> },
      { key: 'liveSession',        label: 'Live Session',        source: 'Paid live session / 1-on-1',                            icon: <FaVideo /> },
      { key: 'consultation',       label: 'Consultation',        source: 'Paid consultation',                                     icon: <FaComments /> },
      { key: 'spotlightPromotion', label: 'Spotlight Promotion', source: 'Share when someone else spotlights this profile',       icon: <FaStar /> },
    ];

    return (
      <div>
        {/* Topline + per-fee-type flat list (#277). Topline cards show
            cumulative earnings; the row list below breaks down the same
            "period" total by source so the user can see where it came
            from at a glance. Sum of row amounts (where available=true)
            equals the Period Earnings card. */}
        {royalties?.stats && (
          <>
            <div className="analytics-summary">
              <div className="summary-stat">
                <span className="stat-label">Period Earnings</span>
                <span className="stat-value">{royalties.stats.period.toFixed(2)} KLV</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Total Earned</span>
                <span className="stat-value">{royalties.stats.total.toFixed(2)} KLV</span>
              </div>
            </div>

            <h3 className="analytics-subsection-title">Earnings by source</h3>
            <div className="earnings-by-type">
              {feeTypeRows.map(({ key, label, source, icon }) => {
                const stat = royalties.stats.byType?.[key];
                const amount = stat?.amount ?? 0;
                const count = stat?.count ?? 0;
                const available = stat?.available ?? true;
                return (
                  <div
                    key={key}
                    className={`earnings-row${!available ? ' earnings-row--unavailable' : ''}${amount === 0 ? ' earnings-row--empty' : ''}`}
                  >
                    <div className="earnings-row__label">
                      <span className="earnings-row__icon">{icon}</span>
                      <div className="earnings-row__text">
                        <div className="earnings-row__title">{label}</div>
                        <div className="earnings-row__source">{source}</div>
                      </div>
                    </div>
                    <div className="earnings-row__value">
                      {available ? (
                        amount > 0 ? (
                          <>
                            <span className="earnings-row__amount">{amount.toFixed(2)} KLV</span>
                            <span className="earnings-row__count">{count} {count === 1 ? 'sale' : 'sales'}</span>
                          </>
                        ) : (
                          <>
                            <span className="earnings-row__amount earnings-row__amount--zero">0 KLV</span>
                            <span className="earnings-row__count">No earnings yet</span>
                          </>
                        )
                      ) : (
                        <span className="earnings-row__pill">Coming soon</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Type Filter */}
        <div className="filter-section" style={{ marginBottom: '1.5rem' }}>
          <label style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem',
            display: 'block'
          }}>
            Royalty Type
          </label>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: 'All Sources', icon: <FaCoins /> },
              { value: 'profile_sales', label: 'Profile Sales', icon: <FaExchangeAlt /> },
              { value: 'spotlights', label: 'All Spotlights', icon: <FaStar /> },
              { value: 'dm_spotlight', label: 'DM Spotlights', icon: <FaStar /> },
              { value: 'post_spotlight', label: 'Post Spotlights', icon: <FaStar /> },
              { value: 'profile_spotlight', label: 'Profile Spotlights', icon: <FaStar /> },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setRoyaltyType(option.value as any)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: royaltyType === option.value ? 'var(--primary-color)' : 'var(--background-secondary)',
                  border: `1px solid ${royaltyType === option.value ? 'var(--primary-color)' : 'var(--border-color)'}`,
                  borderRadius: 'var(--radius-md)',
                  color: royaltyType === option.value ? 'white' : 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="filter-btn"
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Royalties Table */}
        <AnalyticsTable
          data={royalties?.royalties || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No royalties in this time period"
        />
      </div>
    );
  };

  const renderTokenPurchases = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'created_at',
        header: 'Date',
        render: (item) => new Date(item.created_at).toLocaleDateString(),
        width: '120px',
      },
      { key: 'pack_name', header: 'Pack', width: '150px' },
      {
        key: 'token_amount',
        header: 'Tokens',
        render: (item) => `${parseFloat(item.token_amount).toFixed(2)} KLV`,
        width: '140px',
      },
      {
        key: 'usd_price',
        header: 'Price',
        render: (item) => `$${parseFloat(item.usd_price).toFixed(2)}`,
        width: '100px',
      },
      {
        key: 'payment_status',
        header: 'Status',
        render: (item) => (
          <span className={`status-badge ${item.payment_status}`}>
            {item.payment_status}
          </span>
        ),
        width: '120px',
      },
      {
        key: 'tokens_distributed',
        header: 'Distributed',
        render: (item) => (
          <span className={item.tokens_distributed ? 'status-success' : 'status-pending'}>
            {item.tokens_distributed ? 'Yes' : 'No'}
          </span>
        ),
        width: '110px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total Purchases</span>
            <span className="stat-value">{tokenPurchases?.count || 0}</span>
          </div>
        </div>
        <AnalyticsTable
          data={tokenPurchases?.data || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No token purchases in this time period"
        />
      </div>
    );
  };

  const renderTrades = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'created_at',
        header: 'Date',
        render: (item) => new Date(item.created_at).toLocaleString(),
        width: '160px',
      },
      {
        key: 'trade_type',
        header: 'Type',
        render: (item) => (
          <span className={`trade-type ${item.trade_type}`}>
            {item.trade_type}
          </span>
        ),
        width: '100px',
      },
      {
        key: 'from_username',
        header: 'From',
        width: '140px',
        render: (item) => item.from_username ? (
          <Link to={`/profile/${item.from_username}`} className="analytics-user-link">
            {item.from_display_name || item.from_username}
          </Link>
        ) : '—',
      },
      {
        key: 'to_username',
        header: 'To',
        width: '140px',
        render: (item) => item.to_username ? (
          <Link to={`/profile/${item.to_username}`} className="analytics-user-link">
            {item.to_display_name || item.to_username}
          </Link>
        ) : '—',
      },
      {
        key: 'price',
        header: 'Price',
        render: (item) => `${parseFloat(item.price).toFixed(2)} KLV`,
        width: '120px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total Trades</span>
            <span className="stat-value">{trades?.count || 0}</span>
          </div>
        </div>
        <AnalyticsTable
          data={trades?.data || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No trades in this time period"
        />
      </div>
    );
  };

  const renderPosts = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'created_at',
        header: 'Date',
        render: (item) => new Date(item.created_at).toLocaleDateString(),
        width: '120px',
      },
      {
        key: 'body',
        header: 'Post',
        render: (item) => {
          const preview = item.body.substring(0, 60) + (item.body.length > 60 ? '...' : '');
          return (
            <Link to={`/post/${item.id}`} className="analytics-user-link">
              <div className="post-preview">{preview}</div>
            </Link>
          );
        },
      },
      {
        key: 'likes_count',
        header: 'Likes',
        width: '80px',
      },
      {
        key: 'dislikes_count',
        header: 'Dislikes',
        width: '100px',
      },
      {
        key: 'comments_count',
        header: 'Comments',
        width: '100px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total Posts</span>
            <span className="stat-value">{posts?.count || 0}</span>
          </div>
        </div>
        <AnalyticsTable
          data={posts?.data || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No posts in this time period"
        />
      </div>
    );
  };

  const renderMessages = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'partner_username',
        header: 'Conversation',
        render: (item) => item.partner_username ? (
          <Link to={`/profile/@${item.partner_username}`} className="analytics-user-link">
            {item.partner_display_name || item.partner_username}
          </Link>
        ) : '—',
      },
      {
        key: 'total_messages',
        header: 'Total',
        width: '80px',
      },
      {
        key: 'sent',
        header: 'Sent',
        width: '70px',
      },
      {
        key: 'received',
        header: 'Received',
        width: '90px',
      },
      {
        key: 'last_message_at',
        header: 'Last Message',
        render: (item) => new Date(item.last_message_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        width: '130px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total Messages</span>
            <span className="stat-value">{messages?.count || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Sent</span>
            <span className="stat-value">{messages?.sent || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Received</span>
            <span className="stat-value">{messages?.received || 0}</span>
          </div>
        </div>
        <AnalyticsTable
          data={messages?.data || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No messages in this time period"
        />
      </div>
    );
  };

  const renderFollowers = () => {
    const followerColumns: TableColumn<any>[] = [
      {
        key: 'created_at',
        header: 'Date',
        render: (item) => new Date(item.created_at).toLocaleDateString(),
        width: '120px',
      },
      {
        key: 'username',
        header: 'User',
        width: '200px',
        render: (item) => item.username ? (
          <Link to={`/profile/${item.username}`} className="analytics-user-link">
            {item.display_name || item.username}
          </Link>
        ) : '—',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">New Followers</span>
            <span className="stat-value">{followers?.followers.count || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">New Following</span>
            <span className="stat-value">{followers?.following.count || 0}</span>
          </div>
        </div>

        <div className="followers-section">
          <h3>New Followers</h3>
          <AnalyticsTable
            data={followers?.followers.list || []}
            columns={followerColumns}
            isLoading={isLoading}
            emptyMessage="No new followers in this time period"
          />
        </div>

        <div className="followers-section">
          <h3>New Following</h3>
          <AnalyticsTable
            data={followers?.following.list || []}
            columns={followerColumns}
            isLoading={isLoading}
            emptyMessage="No new following in this time period"
          />
        </div>
      </div>
    );
  };

  const renderNotifications = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'created_at',
        header: 'Date',
        render: (item) => new Date(item.created_at).toLocaleString(),
        width: '160px',
      },
      { key: 'type', header: 'Type', width: '120px' },
      { key: 'title', header: 'Title', width: '180px' },
      {
        key: 'message',
        header: 'Message',
        render: (item) => (
          <div className="notification-preview">
            {item.message.substring(0, 50)}
            {item.message.length > 50 ? '...' : ''}
          </div>
        ),
      },
      {
        key: 'read',
        header: 'Status',
        render: (item) => (
          <span className={item.read ? 'status-read' : 'status-unread'}>
            {item.read ? 'Read' : 'Unread'}
          </span>
        ),
        width: '90px',
      },
    ];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total</span>
            <span className="stat-value">{notifications?.count || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Read</span>
            <span className="stat-value">{notifications?.readCount || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Unread</span>
            <span className="stat-value">{notifications?.unreadCount || 0}</span>
          </div>
        </div>
        <AnalyticsTable
          data={notifications?.data || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No notifications in this time period"
        />
      </div>
    );
  };

  const renderEngagement = () => {
    if (!engagement) {
      return (
        <div className="analytics-table-empty">
          <p>{isLoading ? 'Loading...' : 'No engagement data available'}</p>
        </div>
      );
    }

    return (
      <div className="engagement-grid">
        <div className="engagement-card">
          <h3>Likes</h3>
          <div className="engagement-stats">
            <div className="engagement-stat">
              <span className="stat-label">Given</span>
              <span className="stat-value">{engagement.likesGiven}</span>
            </div>
            <div className="engagement-stat">
              <span className="stat-label">Received</span>
              <span className="stat-value">{engagement.likesReceived}</span>
            </div>
          </div>
        </div>

        <div className="engagement-card">
          <h3>Dislikes</h3>
          <div className="engagement-stats">
            <div className="engagement-stat">
              <span className="stat-label">Given</span>
              <span className="stat-value">{engagement.dislikesGiven}</span>
            </div>
            <div className="engagement-stat">
              <span className="stat-label">Received</span>
              <span className="stat-value">{engagement.dislikesReceived}</span>
            </div>
          </div>
        </div>

        <div className="engagement-card">
          <h3>Comments</h3>
          <div className="engagement-stats">
            <div className="engagement-stat">
              <span className="stat-label">Given</span>
              <span className="stat-value">{engagement.commentsGiven}</span>
            </div>
            <div className="engagement-stat">
              <span className="stat-label">Received</span>
              <span className="stat-value">{engagement.commentsReceived}</span>
            </div>
          </div>
        </div>

        <div className="engagement-card">
          <h3>Favorites</h3>
          <div className="engagement-stats">
            <div className="engagement-stat">
              <span className="stat-label">Given</span>
              <span className="stat-value">{engagement.favoritesGiven}</span>
            </div>
            <div className="engagement-stat">
              <span className="stat-label">Received</span>
              <span className="stat-value">{engagement.favoritesReceived}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProfileViews = () => {
    const columns: TableColumn<any>[] = [
      {
        key: 'viewedAt',
        header: 'Date',
        render: (item) => new Date(item.viewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        width: '130px',
      },
      {
        key: 'viewer_username',
        header: 'Viewer',
        render: (item) => item.viewer_username ? (
          <Link to={`/profile/@${item.viewer_username}`} className="analytics-link">
            {item.viewer_display_name || item.viewer_username}
          </Link>
        ) : <span className="text-muted">Anonymous</span>,
      },
      {
        key: 'source',
        header: 'From',
        render: (item) => (
          <span className="source-pill">{formatSource(item.source ?? 'direct')}</span>
        ),
        width: '130px',
      },
    ];

    const bySource = profileViews?.bySource ?? [];
    const totalForBars = bySource.reduce((sum, s) => sum + s.views, 0) || 1;
    const spotlightRoi = profileViews?.spotlightRoi ?? [];

    return (
      <div>
        <div className="analytics-summary">
          <div className="summary-stat">
            <span className="stat-label">Total Views</span>
            <span className="stat-value">{profileViews?.stats.totalViews || 0}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Unique Viewers</span>
            <span className="stat-value">{profileViews?.stats.uniqueViewers || 0}</span>
          </div>
        </div>

        {/* Where viewers come from — bar list, longest on top.
            Hidden when there's no data yet so the empty state isn't
            confusing on accounts with zero historical views. */}
        {bySource.length > 0 && (
          <section className="views-by-source">
            <h3>Where viewers come from</h3>
            <div className="views-by-source__rows">
              {bySource.map((s) => {
                const pct = Math.round((s.views / totalForBars) * 100);
                return (
                  <div key={s.source} className="views-by-source__row">
                    <div className="views-by-source__label">
                      <span className="source-pill">{formatSource(s.source)}</span>
                      <span className="views-by-source__count">
                        {s.views} view{s.views === 1 ? '' : 's'}
                        <span className="views-by-source__sub">
                          · {s.uniqueViewers} unique
                        </span>
                      </span>
                    </div>
                    <div className="views-by-source__bar-track">
                      <div
                        className="views-by-source__bar-fill"
                        style={{ width: `${pct}%` }}
                        aria-label={`${pct}% of views`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Spotlight ROI — only shown when this user has actually paid
            for a profile spotlight. Each row shows what the spotlight
            cost, how many views it pulled, and KLV-per-view. */}
        {spotlightRoi.length > 0 && (
          <section className="spotlight-roi">
            <h3>Spotlight performance</h3>
            <p className="spotlight-roi__hint">
              View attribution for profile spotlights you paid for. Lower KLV/view = better return.
            </p>
            <AnalyticsTable
              data={spotlightRoi}
              columns={[
                {
                  key: 'startTime',
                  header: 'Started',
                  render: (item: any) =>
                    new Date(item.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                  width: '130px',
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (item: any) => {
                    const now = Date.now();
                    const end = new Date(item.endTime).getTime();
                    return (
                      <span className={`status-pill ${end > now ? 'active' : 'ended'}`}>
                        {end > now ? 'Active' : 'Ended'}
                      </span>
                    );
                  },
                  width: '90px',
                },
                {
                  key: 'costKlv',
                  header: 'Cost',
                  render: (item: any) => `${item.costKlv.toFixed(2)} KLV`,
                  width: '110px',
                },
                {
                  key: 'views',
                  header: 'Views',
                  render: (item: any) => item.views,
                  width: '70px',
                },
                {
                  key: 'uniqueViewers',
                  header: 'Unique',
                  render: (item: any) => item.uniqueViewers,
                  width: '70px',
                },
                {
                  key: 'klvPerView',
                  header: 'KLV / view',
                  render: (item: any) =>
                    item.views > 0
                      ? `${(item.costKlv / item.views).toFixed(2)}`
                      : <span className="text-muted">—</span>,
                  width: '100px',
                },
              ]}
              isLoading={false}
              emptyMessage=""
            />
          </section>
        )}

        <h3 className="recent-views-heading">Recent views</h3>
        <AnalyticsTable
          data={profileViews?.views || []}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No profile views in this time period"
        />
      </div>
    );
  };

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        <div className="analytics-header">
          <h1>Analytics</h1>
          <p>View your activity and engagement across the platform</p>
        </div>

        <div className="analytics-controls">
        <div className="category-tabs">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-tab ${currentCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
            >
              {category.icon}
              <span>{category.label}</span>
            </button>
          ))}
        </div>

        <div className="time-range-selector">
          {timeRanges.map((range) => (
            <button
              key={range.value}
              className={`time-range-btn ${currentTimeRange === range.value ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

        <div className="analytics-subheading">
          <span className="analytics-subheading-icon">{activeSubheading.icon}</span>
          <div>
            <h2>{activeSubheading.title}</h2>
            <p>{activeSubheading.description}</p>
          </div>
        </div>

        <div className="analytics-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
